import { inject, Injectable, OnDestroy, signal } from "@angular/core";
import {
  Observable,
  BehaviorSubject,
  of,
  Subscription,
  lastValueFrom,
} from "rxjs";
import { map, catchError, switchMap, finalize } from "rxjs/operators";
import { UserAddress, UserModel } from "../models/user.model";
import { AuthModel, AuthState } from "../models/auth.model";
import { Router } from "@angular/router";
import { LocalStorageService } from "src/app/services/local-storage-service/local-storage.service";
import { HttpClient } from "@angular/common/http";
import { RoleID } from "src/utils/enum/role.enum";
import { ROLE_PERMISSION_MAP } from "src/utils/enum/role-permission.map";
import { NgxPermissionsService } from "ngx-permissions";

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `auth-user`;
  // private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<UserType>;

  currentUser = signal<UserModel | null>(null);
  /** Why the last sign-in failed, verbatim from the API. */
  loginError = signal<string | null>(null);
  role = signal<RoleID>(RoleID.MEMBER);
  userAddress = signal<UserAddress[] | null>(null);

  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  public currentToken: string | null;

  private excepted_key_names: string[] = [];

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  #http = inject(HttpClient);
  #localStorageService = inject(LocalStorageService);

  AuthStates = AuthState;
  authState = signal<any>(this.AuthStates.UNAUTHORIZED);

  constructor(
    private router: Router,
    private permissionsService: NgxPermissionsService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  _setFilter(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v != null)
    );
  }

  _setFormData(object_key: string, value: any) {
    let formData = new FormData();
    for (const key in value) {
      let obj = value[key];
      if (Array.isArray(obj)) {
        obj.forEach((e, i) => {
          if (this.excepted_key_names.includes(key)) {
            formData.append(`${object_key}[${key}][]`, e);
          } else {
            for (const key_i in e) {
              formData.append(`${object_key}[${key}][][${key_i}]`, e[key_i]);
            }
          }
        });
      } else {
        formData.append(`${object_key}[${key}]`, value[key]);
      }
    }
    return formData;
  }

  async setUser(user: any) {
    this.currentUser.update((_) => user);
    this.#localStorageService.setItem(
      this.authLocalStorageToken,
      JSON.stringify(user)
    );
    this.setUserRole(user.user.role.id);
    const addresses = (await lastValueFrom(
      this.#http.get("/addresses/current").pipe()
    )) as UserAddress[];

    this.userAddress.update((_) => addresses);
  }

  setUserRole(roleId: number) {
    this.role.set(roleId as RoleID);
    const permissions = ROLE_PERMISSION_MAP[this.role()] ?? [];
    this.permissionsService.flushPermissions();
    this.permissionsService.loadPermissions(permissions);
  }

  // public methods
  login(payload: { username: string; password: string }): Observable<UserType> {
    this.isLoadingSubject.next(true);
    this.loginError.set(null);
    return this.#http.post("/sessions", payload).pipe(
      map((res: any) => {
        const result = this.setAuthFromLocalStorage(res);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        // The API distinguishes a wrong password from an account that is
        // awaiting approval or has been rejected. Swallowing all three into
        // `undefined` told everyone their password was wrong and left the other
        // two with nothing to act on. Callers still get undefined; the reason
        // is kept here for whoever renders the failure.
        this.loginError.set(err?.error?.errors ?? null);
        return of(undefined);
      }),
      finalize(() =>
        setTimeout(() => {
          this.isLoadingSubject.next(false);
        }, 500)
      )
    );
  }

  logout() {
    document.location.href = "/home";
    this.#localStorageService.removeItem(this.authLocalStorageToken);
  }

  getUserByToken(): Observable<UserType> {
    let auth = this.getAuthFromLocalStorage() as any;
    if (!auth || !auth.access_token) {
      return of(undefined);
    }
    this.isLoadingSubject.next(true);
    return this.#http.get("/users/current").pipe(
      map(
        (user: any) => {
          if (user) {
            this.currentUserSubject.next(user);
            auth.user = user;
            this.setUser(auth);
          } else {
            this.logout();
          }
          return user;
        },
        (error: any) => { }
      ),
      finalize(() =>
        setTimeout(() => {
          // this.setUser(auth);
          this.isLoadingSubject.next(false);
        }, 200)
      )
    );
  }

  // need create new user then login
  registration(payload: UserModel): Observable<any> {
    const registerType = payload.is_student ? "student" : "parent";
    let normalizedPayload = this._setFilter(payload)
    const formData = this._setFormData(registerType, normalizedPayload);
    this.isLoadingSubject.next(true);
    return this.#http.post(`/${registerType}s`, formData).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(payload)),
      // No catchError: a rejected sign-up (email or name already taken) must
      // reach RegistrationComponent.submit()'s catch so it can say why.
      // Swallowing it into `undefined` left the form sitting there silently.
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string) {
    // forgotPassword(email: string): Observable<boolean> {
    // this.isLoadingSubject.next(true);
    // return this.authHttpService
    //   .forgotPassword(email)
    //   .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/expires In in local storage to keep user logged in between page refreshes
    if (auth && auth.access_token) {
      this.setUser(auth);
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = this.#localStorageService.getItem(
        this.authLocalStorageToken
      );

      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  updateAuthState(state: AuthState) {
    this.authState.update((_) => state);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
